/**
 * Preferences UI config — progress, labels, header styles.
 *
 * Belongs here: display config and helpers.
 * Does not belong here: flow rules.
 */
import { PreferenceTypeId, PreferenceQuestionType } from './types'
import type { FundPreferenceType, PreferencesAnswersMap } from './types'
import { isNonCorePreferenceType } from './preferenceTypeUtils'
import {
  normalizePlanPercentage,
  planPercentageLabel
} from '../../../plans-v4/display-values/lumpsumPlanPercentage'

const DEFAULT_DISCLAIMER_COLOR = '#232323'
const NON_CORE_DISCLAIMER_COLOR = '#992E00'

const THEME_GOLD_AND_SILVER = {
  background: 'linear-gradient(98deg, #F8F6DF 7.09%, #FFEFC2 101.18%)',
  textColor: '#232323',
  disclaimerTextColor: DEFAULT_DISCLAIMER_COLOR
}

const THEME_GLOBAL = {
  background: 'linear-gradient(98deg, #DFF8F2 7.09%, #C2FFEF 101.18%)',
  textColor: '#232323',
  disclaimerTextColor: DEFAULT_DISCLAIMER_COLOR
}

const THEME_NON_CORE = {
  background: 'linear-gradient(98deg, #F8E4DF 7.09%, #FFCDC2 101.18%)',
  textColor: '#232323',
  disclaimerTextColor: NON_CORE_DISCLAIMER_COLOR
}

const THEME_DEBT = {
  background: 'linear-gradient(98deg, #EEF8DF 7.09%, #E6FFC2 101.18%)',
  textColor: '#232323',
  disclaimerTextColor: DEFAULT_DISCLAIMER_COLOR
}

const THEME_SATELLITE = {
  background: 'linear-gradient(93.68deg, #FFEAE5 7.09%, #F2C2B9 101.18%)',
  textColor: '#232323',
  disclaimerTextColor: DEFAULT_DISCLAIMER_COLOR
}

const THEME_DEFAULT = {
  background: 'linear-gradient(98deg, #EBF2F2 7.09%, #DEE5E5 101.18%)',
  textColor: '#232323',
  disclaimerTextColor: DEFAULT_DISCLAIMER_COLOR
}

function getPreferenceTypeTheme(preferenceTypeId: string): typeof THEME_DEFAULT {
  if (preferenceTypeId === PreferenceTypeId.SILVER_AND_GOLD) return THEME_GOLD_AND_SILVER
  if (
    preferenceTypeId === PreferenceTypeId.GLOBAL_US ||
    preferenceTypeId === PreferenceTypeId.GLOBAL_NON_CORE ||
    preferenceTypeId === PreferenceTypeId.GLOBAL_OTHER
  ) {
    return THEME_GLOBAL
  }
  if (
    preferenceTypeId === PreferenceTypeId.DEBT_AND_ARBITRAGE ||
    preferenceTypeId === PreferenceTypeId.DEBT
  ) {
    return THEME_DEBT
  }
  if (preferenceTypeId === PreferenceTypeId.SATELLITE) return THEME_SATELLITE
  if (isNonCorePreferenceType(preferenceTypeId)) return THEME_NON_CORE
  return THEME_DEFAULT
}

export function getPreferenceTypeHeaderStyle(preferenceTypeId: string): { background: string; color: string } {
  const theme = getPreferenceTypeTheme(preferenceTypeId)
  return { background: theme.background, color: theme.textColor }
}

export function getPreferenceTypeDisclaimerColor(preferenceTypeId: string): string {
  return getPreferenceTypeTheme(preferenceTypeId).disclaimerTextColor
}

/** Computes progress value (0–1) for preferences questionnaire. */
export function computePreferencesProgressValue(
  preferenceTypes: FundPreferenceType[],
  preferenceTypeIndex: number,
  questionIndex: number,
  visibleCountInCurrentType: number,
  isEditOnePreferenceType: boolean,
  getVisibleQuestionsForPreferenceType: (preferenceTypeId: string) => { length: number }
): number {
  if (isEditOnePreferenceType) return visibleCountInCurrentType > 0 ? (questionIndex + 1) / visibleCountInCurrentType : 0
  let total = 0
  for (const pt of preferenceTypes) total += getVisibleQuestionsForPreferenceType(pt.id).length
  let before = 0
  for (let idx = 0; idx < preferenceTypeIndex; idx++) before += getVisibleQuestionsForPreferenceType(preferenceTypes[idx].id).length
  return total > 0 ? (before + questionIndex + 1) / total : 0
}

export function isCurrentQuestionAnswered(
  questionId: string | null,
  answers: PreferencesAnswersMap,
  inputType?: string,
  questionType?: string
): boolean {
  if (questionType === PreferenceQuestionType.SEGMENT_INTRO || inputType === 'MULTI_SELECT_GROUPS') {
    return true
  }
  if (questionId == null) return false
  const value = answers[questionId]
  if (value == null) return false
  if (Array.isArray(value)) {
    return value.length > 0
  }
  if (value === '') return false
  if (typeof value === 'number' && Number.isNaN(value)) return false
  return true
}

export type PortfolioFundLabel =
  | { kind: 'plain'; text: string }
  | { kind: 'composed'; portfolioPart: string; fundPart: string }

/** Builds portfolio-share + fund-count label parts (separator is SVG in UI). */
export function formatPortfolioShareAndFundCount(
  portfolioShare: number | undefined,
  fundCount: number
): PortfolioFundLabel {
  if (fundCount === 0) return { kind: 'plain', text: 'No fund detected' }
  const fundText = fundCount === 1 ? '1 fund' : `${fundCount} funds`
  if (portfolioShare == null) return { kind: 'plain', text: fundText }
  return {
    kind: 'composed',
    portfolioPart: `${planPercentageLabel({ rawPercentage: portfolioShare })} of portfolio`,
    fundPart: fundText
  }
}

export function getQuestionFundCount(question: {
  fundList?: unknown[] | null
}): number {
  return question.fundList?.length ?? 0
}

export function getQuestionPortfolioShare(question: {
  portfolioShare?: number | null
}): number | undefined {
  if (typeof question.portfolioShare === 'number') return question.portfolioShare
  return undefined
}

export function formatIdealAllocation(
  ideal: { absolute?: number | null; min?: number | null; max?: number | null } | null | undefined
): string {
  const minPercent = ideal?.min
  const maxPercent = ideal?.max
  if (typeof minPercent === 'number' && typeof maxPercent === 'number') {
    const minLabel = planPercentageLabel({ rawPercentage: minPercent })
    const maxLabel = planPercentageLabel({ rawPercentage: maxPercent })
    // Compare the rounded labels. 4.6 and 5.4 both read as 5%, so that is one percent.
    if (minLabel === maxLabel) {
      return maxLabel
    }
    return `${minLabel.slice(0, -1)}–${maxLabel}`
  }
  const absolutePercent = ideal?.absolute
  if (typeof absolutePercent === 'number') {
    return planPercentageLabel({ rawPercentage: absolutePercent })
  }
  return ''
}

export function getIdealMaxPercent(
  ideal: { absolute?: number | null; max?: number | null } | null | undefined
): number | undefined {
  if (typeof ideal?.max === 'number') return normalizePlanPercentage(ideal.max)
  return undefined
}

export function getPreferencesNextButtonLabel(
  inputType: string | undefined,
  isComplete: boolean,
  insightNextButtonLabel?: string,
  questionType?: string
): string {
  if (questionType === PreferenceQuestionType.SEGMENT_INTRO) {
    return insightNextButtonLabel || 'Next'
  }
  if (inputType === 'MULTI_SELECT_GROUPS') {
    if (isComplete) return 'Continue to review'
    return 'Save and continue'
  }
  if (isComplete) return 'Continue to review'
  return 'Next'
}

export function getFundLabelForPreferenceType(preferenceType: FundPreferenceType): PortfolioFundLabel {
  return formatPortfolioShareAndFundCount(preferenceType.portfolioPercent, preferenceType.fundCount)
}
